// App del camionero — reparto de un viaje de camión/gira.
// Lista los pedidos cargados del viaje. El camionero escanea los bultos/paquetes
// de cada pedido (mismo QR que la carga) y al completar se registra la ENTREGA
// (entregarPedidoDespachado → pedidos_entregados). Fase 1: solo camión/gira.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { doc, updateDoc, collection, query, where, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeProvider, useTheme } from "../../../theme/ThemeProvider";
import { AppHeader, Card, Button, Chip, BottomBar } from "../../../theme/ui";
import { unidadesDeItem } from "../services/etiquetasFS";
import { entregarPedidoDespachado } from "../services/pedidosFS";
import QrScanner from "../../../components/QrScanner";

// ── Modal de entrega: escanear bultos/paquetes → registrar entrega ──
function EntregarModal({ pedido, uid, nombre, onClose, onEntregado }) {
  const esAgregado = pedido.agregado === true;
  const unidades = useMemo(
    () => esAgregado ? [] : unidadesDeItem({ pedidoId: pedido.id, bultos: pedido.bultos, paquetes: pedido.paquetes }),
    [esAgregado, pedido.id, pedido.bultos, pedido.paquetes]
  );
  const total = esAgregado ? 0 : (unidades.length || 1);
  const [escaneados, setEscaneados] = useState(new Set());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleScan = useCallback((raw) => {
    setError("");
    const texto = (typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw)).trim();
    const u = unidades.find(x => x.key === texto);
    if (!u) { setError(texto.startsWith(pedido.id) ? "Etiqueta que no corresponde a este pedido" : "Etiqueta de otro pedido"); return; }
    setEscaneados(prev => { if (prev.has(u.key)) { setError(`${u.label} ya escaneado`); return prev; } return new Set([...prev, u.key]); });
  }, [pedido.id, unidades]);

  const todos = esAgregado || escaneados.size === total;

  async function confirmar() {
    setSaving(true);
    try {
      await entregarPedidoDespachado(pedido.id, { uid, nombre, bultosEscaneados: [...escaneados] });
      onEntregado();
    } catch (e) { setError("Error al registrar la entrega: " + (e?.message || e)); setSaving(false); }
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", width: "100%", borderRadius: "16px 16px 0 0", padding: "20px 20px 28px", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>📷 Entregar — #{pedido.numero}</h3>
            <p style={{ margin: "3px 0 0", fontSize: 13, color: "#64748b" }}>{pedido.cliente}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
        </div>

        {!esAgregado && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {unidades.map(u => {
              const ok = escaneados.has(u.key);
              const esPaq = u.tipo === "paquete";
              return (
                <div key={u.key} title={u.label} style={{
                  minWidth: 36, height: 36, padding: "0 6px", borderRadius: 8,
                  background: ok ? "#16a34a" : esPaq ? "#eff6ff" : "#f1f5f9",
                  color: ok ? "#fff" : esPaq ? "#1d4ed8" : "#94a3b8",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 13, border: `1.5px solid ${ok ? "#16a34a" : esPaq ? "#bfdbfe" : "#e2e8f0"}`,
                }}>{ok ? "✓" : (esPaq ? "📏" : "📦") + u.indice}</div>
              );
            })}
          </div>
        )}
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
          {esAgregado ? "Pedido agregado — va con otro pedido, sin escaneo." : `${escaneados.size}/${total} unidades escaneadas (${Number(pedido.bultos) || 0} bultos · ${Number(pedido.paquetes) || 0} paquetes)`}
        </p>
        {error && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, fontWeight: 600, marginBottom: 12 }}>⚠️ {error}</div>
        )}
        {!todos ? (
          <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />
        ) : (
          <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "14px 16px", marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
            <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: 15 }}>{esAgregado ? "Listo para entregar" : "Todas las unidades escaneadas"}</p>
          </div>
        )}
        <button onClick={confirmar} disabled={!todos || saving} style={{
          width: "100%", padding: 15, borderRadius: 12, border: "none", fontWeight: 700, fontSize: 15, marginTop: 8,
          background: todos && !saving ? "#15803d" : "#e2e8f0", color: todos && !saving ? "#fff" : "#94a3b8",
          cursor: todos && !saving ? "pointer" : "not-allowed",
        }}>{saving ? "Registrando…" : "✓ Confirmar entrega"}</button>
      </div>
    </div>
  );
}

function RepartoInner() {
  const { camionId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const t = useTheme();
  const uid = profile?.id || profile?.uid || null;
  const nombre = profile?.nombre || profile?.displayName || "Camionero";

  const [viaje, setViaje] = useState(null);
  const [pendientes, setPendientes] = useState([]);   // despachados CARGADO del viaje
  const [entregados, setEntregados] = useState(0);     // ya entregados de este viaje
  const [scan, setScan] = useState(null);              // pedido en escaneo
  const [cerrando, setCerrando] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "camiones", camionId), s => setViaje(s.exists() ? { id: s.id, ...s.data() } : null));
    return () => unsub();
  }, [camionId]);

  useEffect(() => {
    const q = query(collection(db, "pedidos_despachados"), where("camionId", "==", camionId));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.cargaEstado === "CARGADO" && p.estado !== "ANULADO");
      setPendientes(docs);
    });
    return () => unsub();
  }, [camionId]);

  useEffect(() => {
    const q = query(collection(db, "pedidos_entregados"), where("camionId", "==", camionId));
    const unsub = onSnapshot(q, snap => setEntregados(snap.size), () => {});
    return () => unsub();
  }, [camionId]);

  const totalViaje = pendientes.length + entregados;
  const progreso = totalViaje > 0 ? entregados / totalViaje : 0;
  const completo = pendientes.length === 0 && entregados > 0;

  async function cerrarViaje() {
    setCerrando(true);
    try {
      await updateDoc(doc(db, "camiones", camionId), { estado: "ENTREGADO", repartoFinAt: serverTimestamp(), totalEntregados: entregados });
      navigate("/camionero/viajes");
    } catch (e) { setCerrando(false); alert("No se pudo cerrar el viaje: " + (e?.message || e)); }
  }

  return (
    <div style={{ minHeight: "100vh", background: t.color.bg, color: t.color.text }}>
      <AppHeader
        icon={viaje?.tipo === "CAMION" ? "🚛" : "🚚"}
        title={viaje?.descripcion || "Reparto"}
        subtitle={`${entregados}/${totalViaje} entregados`}
        progress={progreso}
        right={<Button size="sm" variant="ghost" onClick={() => navigate("/camionero/viajes")}>‹ Volver</Button>}
      />
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12, paddingBottom: completo ? 96 : 16 }}>
        {pendientes.length === 0 ? (
          <Card tone={completo ? "success" : "default"} style={{ flexDirection: "column", alignItems: "center", textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{completo ? "🎉" : "📦"}</div>
            <div style={{ fontWeight: 700 }}>{completo ? "¡Todo entregado!" : "No hay pedidos para entregar"}</div>
            <div style={{ fontSize: t.font.sm, color: t.color.textMuted, marginTop: 4 }}>
              {completo ? "Cerrá el viaje para finalizarlo." : "Los pedidos cargados de este viaje aparecen acá."}
            </div>
          </Card>
        ) : (
          pendientes.map(p => (
            <Card key={p.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: t.font.lg }}>#{p.numero}</span>
                  <Chip tone="neutral">{(Number(p.bultos) || 0)}📦 · {(Number(p.paquetes) || 0)}📏</Chip>
                </div>
                <div style={{ fontSize: t.font.base, color: t.color.text, marginTop: 3 }}>{p.cliente || "—"}</div>
              </div>
              <Button variant="success" onClick={() => setScan(p)}>Entregar</Button>
            </Card>
          ))
        )}
      </div>

      {completo && (
        <BottomBar>
          <span style={{ fontSize: t.font.sm, color: t.color.textMuted }}>{entregados} pedidos entregados</span>
          <Button variant="primary" onClick={cerrarViaje} disabled={cerrando}>{cerrando ? "Cerrando…" : "Cerrar viaje"}</Button>
        </BottomBar>
      )}

      {scan && (
        <EntregarModal
          pedido={scan}
          uid={uid}
          nombre={nombre}
          onClose={() => setScan(null)}
          onEntregado={() => setScan(null)}
        />
      )}
    </div>
  );
}

export default function CamioneroReparto() {
  return (
    <ThemeProvider mode="light">
      <RepartoInner />
    </ThemeProvider>
  );
}
